import { cn } from "../../lib/utils";
import type { LucideIcon } from "lucide-react";
import {
  Leaf,
  Mountain,
  Landmark,
  Dumbbell,
  PartyPopper,
  UtensilsCrossed,
  Baby,
  TreePine,
  Palette,
  MoreHorizontal,
} from "lucide-react";

interface CategoryOption {
  value: string;
  label: string;
  icon: LucideIcon;
  color: string;
}

const CATEGORIES: CategoryOption[] = [
  { value: "wellness", label: "Benessere", icon: Leaf, color: "#10b981" },
  { value: "outdoor", label: "Outdoor", icon: Mountain, color: "#f59e0b" },
  { value: "culture", label: "Cultura", icon: Landmark, color: "#8b5cf6" },
  { value: "sport", label: "Sport", icon: Dumbbell, color: "#ef4444" },
  { value: "entertainment", label: "Intrattenimento", icon: PartyPopper, color: "#ec4899" },
  { value: "gastronomy", label: "Gastronomia", icon: UtensilsCrossed, color: "#f97316" },
  { value: "family", label: "Famiglia", icon: Baby, color: "#06b6d4" },
  { value: "nature", label: "Natura", icon: TreePine, color: "#22c55e" },
  { value: "art", label: "Arte", icon: Palette, color: "#a855f7" },
  { value: "other", label: "Altro", icon: MoreHorizontal, color: "#64748b" },
];

interface CategorySelectProps {
  value: string | null;
  onChange: (value: string | null) => void;
  className?: string;
  disabled?: boolean;
}

export function CategorySelect({
  value,
  onChange,
  className,
  disabled = false,
}: CategorySelectProps) {
  const handleSelect = (categoryValue: string) => {
    if (disabled) return;
    // Toggle selection
    onChange(value === categoryValue ? null : categoryValue);
  };

  return (
    <div className={cn("space-y-2", className)}>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {CATEGORIES.map((category) => {
          const Icon = category.icon;
          const isSelected = value === category.value;

          return (
            <button
              key={category.value}
              type="button"
              onClick={() => handleSelect(category.value)}
              disabled={disabled}
              className={cn(
                "flex flex-col items-center gap-1.5 p-3 rounded-lg border text-center transition-all",
                isSelected
                  ? "border-2 shadow-sm"
                  : "border-border hover:border-[#284899]/30",
                disabled && "opacity-50 cursor-not-allowed"
              )}
              style={{
                borderColor: isSelected ? category.color : undefined,
                backgroundColor: isSelected ? `${category.color}10` : undefined,
              }}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${category.color}20` }}
              >
                <Icon
                  className="h-4 w-4"
                  style={{ color: category.color }}
                />
              </div>
              <span
                className={cn(
                  "text-sm font-medium",
                  isSelected ? "text-foreground" : "text-muted-foreground"
                )}
              >
                {category.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Export categories for use in other components
export { CATEGORIES };
export type { CategoryOption };
